import React, { useState, useEffect } from 'react';
import { Hooks } from "@xt/xtbase";
import { isLandingPathname } from '@/utils/landingRoute';
const { useTranslation } = Hooks;

const PWAUpdatePrompt = () => {
  const { t } = useTranslation();
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    // 检查浏览器支持
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    // 注册更新回调
    const handleUpdate = (hasUpdate) => {
      if (hasUpdate) {
        setShowUpdatePrompt(true);
      }
    };

    // 使用全局更新检查方法
    let unsubscribe = () => {};
    if (window.__swOnUpdate) {
      unsubscribe = window.__swOnUpdate(handleUpdate);
    }

    // 定期检查更新（每30秒）
    const updateInterval = setInterval(() => {
      if (window.__swUpdateCheck) {
        window.__swUpdateCheck();
      }
    }, 30000);

    // 页面可见时检查更新
    const handleVisibilityChange = () => {
      if (!document.hidden && window.__swUpdateCheck) {
        window.__swUpdateCheck();
      }
    };

    // 页面获得焦点时检查更新
    const handleFocus = () => {
      if (window.__swUpdateCheck) {
        window.__swUpdateCheck();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      unsubscribe();
      clearInterval(updateInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const handleUpdate = async () => {
    if (isUpdating) return;
    
    setIsUpdating(true);
    
    try {
      // 获取 Service Worker 注册
      const registration = await navigator.serviceWorker.getRegistration();
      
      if (registration && registration.waiting) {
        // 发送 skipWaiting 消息给等待中的 Service Worker
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        
        // 等待控制权变更
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          // 刷新页面以使用新版本
          window.location.reload();
        }, { once: true });

        // 如果 1 秒后还没有控制权变更，直接刷新
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        // 如果没有等待中的 Service Worker，直接刷新
        window.location.reload();
      }
    } catch (error) {
      console.error('[PWA] 更新失败:', error);
      setIsUpdating(false);
      // 更新失败时也尝试刷新
      window.location.reload();
    }
  };

  const handleDismiss = () => {
    setShowUpdatePrompt(false);
  };

  if (
    typeof window !== 'undefined' &&
    isLandingPathname(window.location.pathname)
  ) {
    return null;
  }

  if (!showUpdatePrompt) {
    return null;
  }

  return (
    <>
      <style>{`
        @keyframes slideDown {
          from {
            transform: translateX(-50%) translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
        .pwa-update-prompt {
          position: fixed;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 10001;
          max-width: 90%;
          width: 400px;
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          animation: slideDown 0.3s ease-out;
          padding: 16px;
          box-sizing: border-box;
        }
        [data-theme='dark'] .pwa-update-prompt {
          background: #1B1B1E;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        [data-theme='dark'] .pwa-update-prompt .pwa-update-title {
          color: #fff;
        }
        [data-theme='dark'] .pwa-update-prompt .pwa-update-desc {
          color: rgba(255, 255, 255, 0.6);
        }
        .pwa-update-content {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .pwa-update-title {
          font-size: 16px;
          font-weight: 600;
          color: #333;
          margin: 0;
        }
        .pwa-update-desc {
          font-size: 14px;
          color: #666;
          line-height: 1.5;
          margin: 0;
        }
        .pwa-update-actions {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
          margin-top: 8px;
        }
        .pwa-update-btn {
          padding: 8px 20px;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }
        .pwa-update-btn-primary {
          background: #B9F78A;
          color: #000;
        }
        .pwa-update-btn-primary:hover {
          background: #3aa865;
          transform: scale(1.02);
        }
        .pwa-update-btn-primary:active {
          transform: scale(0.98);
        }
        .pwa-update-btn-secondary {
          background: transparent;
          color: #999;
        }
        .pwa-update-btn-secondary:hover {
          background: rgba(0, 0, 0, 0.05);
          color: #333;
        }
        [data-theme='dark'] .pwa-update-btn-secondary:hover {
          background: rgba(255, 255, 255, 0.1);
          color: #fff;
        }
        .pwa-update-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        @media (max-width: 767px) {
          .pwa-update-prompt {
            width: calc(100% - 32px);
            max-width: none;
            top: 16px;
            padding: 14px;
          }
          .pwa-update-title {
            font-size: 15px;
          }
          .pwa-update-desc {
            font-size: 13px;
          }
          .pwa-update-actions {
            flex-direction: column;
          }
          .pwa-update-btn {
            width: 100%;
            padding: 10px 20px;
          }
        }
      `}</style>
      <div className="pwa-update-prompt">
        <div className="pwa-update-content">
          <h3 className="pwa-update-title">
            {t('pwa.update.title') || '新版本可用'}
          </h3>
          <p className="pwa-update-desc">
            {t('pwa.update.description') || '发现新版本，请刷新页面以获取最新功能。'}
          </p>
          <div className="pwa-update-actions">
            <button
              className="pwa-update-btn pwa-update-btn-secondary"
              onClick={handleDismiss}
              disabled={isUpdating}
            >
              {t('pwa.update.later') || '稍后'}
            </button>
            <button
              className="pwa-update-btn pwa-update-btn-primary"
              onClick={handleUpdate}
              disabled={isUpdating}
            >
              {isUpdating 
                ? (t('pwa.update.updating') || '更新中...') 
                : (t('pwa.update.button') || '立即更新')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PWAUpdatePrompt;

